import { z } from 'zod';
import { zNonEmpty, zUuid } from '@saas/shared';
import { ACTIVITY_TYPES, zCreateContact, zCreateCustomer } from './crm.dto.js';
import type { LeadOwnerStatus } from './crm-handover.dto.js';

/**
 * SFA 段階 1A の契約（SFA機能拡張設計書 §5・§10.2〜10.3・§15.1）。
 * 見込み客（crm_leads）・問い合わせ（crm_inquiries）・履歴（crm_lead_history）・公開フォーム（crm_forms / crm_form_versions）・
 * 割当規則（crm_lead_assignment_rules）・取込と一括操作・見込み客の設定。
 * ★ リードが固定した契約。追加は可・改名と意味の変更は不可（変えたいときは報告に書く）。
 */

const zOptionalText = (max: number) => z.string().trim().max(max).nullable().optional();
const zQueryBool = z.preprocess((v) => (typeof v === 'string' ? v === '1' || v.toLowerCase() === 'true' : v), z.boolean()).optional();

// ---- 列挙 ----

/**
 * 見込み客の状態（§5.3）。new → assigned → working → qualified → converted。
 * closed = 対応終了（理由を記録）、spam = 迷惑送信。closed → working は「再開」（理由を記録）。
 */
export const LEAD_STATUSES = ['new', 'assigned', 'working', 'qualified', 'converted', 'closed', 'spam'] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** 対応終了の理由（§5.3）。自由記述は reasonText に残す */
export const LEAD_CLOSE_REASONS = ['no_need', 'no_response', 'budget', 'timing', 'competitor', 'duplicate', 'other'] as const;
export type LeadCloseReason = (typeof LEAD_CLOSE_REASONS)[number];

/** 問い合わせ・見込み客の入口（§1 の図・§5.5・§5.6）。form = 公開フォーム / email = 転送受付 / csv = 名簿取込 */
export const INQUIRY_CHANNELS = ['form', 'email', 'phone', 'card', 'csv', 'manual', 'api'] as const;
export type InquiryChannel = (typeof INQUIRY_CHANNELS)[number];

/** 問い合わせ種別の初期値（§5.1）。会社ごとに変更できるので DB は文字列。画面はこの並びを既定に出す */
export const DEFAULT_INQUIRY_TYPES = ['document_request', 'quote', 'consultation', 'other'] as const;

/** 活動の方向と結果（§15.2.3）。既存の activities に列を足す */
export const ACTIVITY_DIRECTIONS = ['outbound', 'inbound'] as const;
export type ActivityDirection = (typeof ACTIVITY_DIRECTIONS)[number];
export const ACTIVITY_OUTCOMES = ['reached', 'no_answer', 'replied', 'none'] as const;
export type ActivityOutcome = (typeof ACTIVITY_OUTCOMES)[number];

export const LEAD_FORM_STATUSES = ['draft', 'published', 'stopped'] as const;
export type LeadFormStatus = (typeof LEAD_FORM_STATUSES)[number];

export const LEAD_FORM_FIELD_TYPES = ['text', 'textarea', 'email', 'tel', 'select', 'checkbox'] as const;
export type LeadFormFieldType = (typeof LEAD_FORM_FIELD_TYPES)[number];

/** 標準項目のキー（§5.1）。氏名・メール・内容は必須固定。それ以外は管理者が表示・必須を設定 */
export const LEAD_FORM_STANDARD_FIELDS = ['companyName', 'displayName', 'email', 'body', 'phone', 'departmentName', 'title', 'desiredTiming', 'budgetRange', 'inquiryType'] as const;

export const LEAD_ASSIGNMENT_MODES = ['fixed', 'round_robin', 'none'] as const;
export type LeadAssignmentMode = (typeof LEAD_ASSIGNMENT_MODES)[number];

export const LEAD_BOT_CHECKS = ['none', 'turnstile', 'recaptcha'] as const;
export type LeadBotCheck = (typeof LEAD_BOT_CHECKS)[number];

// ---- サマリ DTO ----

export interface LeadSummary {
  id: string;
  displayName: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  status: LeadStatus;
  closeReason: LeadCloseReason | null;
  inquiryType: string | null;
  /** 初回の流入元（§5.1「流入元」）。sourceLabel は展示会名・参照元など */
  sourceChannel: InquiryChannel;
  sourceLabel: string | null;
  ownerId: string | null;
  ownerName: string | null;
  /** 受付先チーム（部署）。未割当の見込み客をこの部署の責任者に見せる（§5.2） */
  teamDepartmentId: string | null;
  customerId: string | null;
  contactId: string | null;
  /** 初回の商談化で作った商談。関連商談の追加はこれを変えない（§5.4） */
  convertedDealId: string | null;
  convertedAt: Date | null;
  /** 初回対応の期限と実績（§5.2・§15.2.1 の営業時間で計算） */
  firstResponseDueAt: Date | null;
  firstRespondedAt: Date | null;
  /** 最終接点（§15.2.3 の「最終接点に数える」活動の最新） */
  lastTouchedAt: Date | null;
  inquiryCount: number;
  /** 楽観ロック（§10.3「版番号による競合検知」） */
  version: number;
  createdAt: Date;
  updatedAt: Date;
  // ---- SFA 段階 3（追加のみ・任意）: 担当者の状態と優先度 ----
  /** 担当者の状態（利用停止中なら責任者の一覧に「担当者が利用停止」と出す・§15.3.2）。担当なしは null */
  ownerStatus?: LeadOwnerStatus | null;
  /** 優先度（§15.5 の規則。規則が無ければ normal）と加点の合計 */
  priority?: LeadPriority;
  priorityScore?: number;
}

export interface InquirySummary {
  id: string;
  leadId: string;
  channel: InquiryChannel;
  formId: string | null;
  formVersion: number | null;
  /** 原文（フォームの入力値 / メール本文 / 手入力）。個人情報を含む。knowledge には電話・メールを伏せて載せる（2026-10-03 の決定・crm の提供者 inquiry） */
  body: string | null;
  subject: string | null;
  inquiryType: string | null;
  /** 送信 ID（同じ送信の再試行を 1 件にまとめる・§5.3）。メールは受信 ID */
  submissionId: string | null;
  referrer: string | null;
  utm: Record<string, string> | null;
  /** フォームの入力値（項目キー → 値）。標準項目以外を含む */
  fields: Record<string, string | boolean> | null;
  receivedAt: Date;
  /** この問い合わせへの対応（§5.2「どの問い合わせへの対応か」）。最初の有効な対応の活動 */
  respondedAt: Date | null;
  respondedActivityId: string | null;
  /**
   * （gap-crm-b の追加・任意）公開フォームを iframe で埋め込んだ会社のサイトのページ（クエリと # を落とした URL・decisions §AC の残課題）。
   * 埋め込み用のコードが iframe の URL に付けて渡す。埋め込みでない送信・フォーム以外は null。匿名化で消す
   */
  pageUrl?: string | null;
}

export const LEAD_HISTORY_KINDS = ['created', 'status', 'assigned', 'merged', 'converted', 'reopened', 'bulk', 'note', 'imported', 'responded'] as const;
export type LeadHistoryKind = (typeof LEAD_HISTORY_KINDS)[number];

export interface LeadHistoryEntry {
  id: string;
  leadId: string;
  kind: LeadHistoryKind;
  from: string | null;
  to: string | null;
  reason: string | null;
  actorId: string | null;
  actorName: string | null;
  at: Date;
}

/** 重複候補（§5.3）。exact = 確認済み連絡先のメール完全一致（最優先）/ candidate = 会社名・ドメイン・電話の一致（表示だけ） */
export interface LeadDuplicateCandidate {
  kind: 'lead' | 'contact' | 'customer';
  id: string;
  label: string;
  matchedBy: 'email' | 'phone' | 'company' | 'domain';
  confidence: 'exact' | 'candidate';
}

export interface LeadDetail extends LeadSummary {
  title: string | null;
  departmentName: string | null;
  address: string | null;
  website: string | null;
  desiredTiming: string | null;
  budgetRange: string | null;
  notes: string | null;
  /** メール配信への同意（受付と独立・§5.1）。null = 未回答 */
  consentMarketing: boolean | null;
  consentAt: Date | null;
  privacyNoticeVersion: string | null;
  closeReasonText: string | null;
  customerName: string | null;
  contactName: string | null;
  inquiries: InquirySummary[];
  history: LeadHistoryEntry[];
  duplicates: LeadDuplicateCandidate[];
  /** 関連商談（初回の商談化 + 関連商談の追加）。sales の deals.lead_id で引く */
  relatedDealIds: string[];
  /** （crm-leads-server の追加）個人情報を匿名化した時刻（保持期間・削除請求・§15.2.4）。画面は「匿名化済み」を出し、編集させない */
  anonymizedAt?: Date | null;
  /** （crm-leads-server の追加）統合された見込み客なら統合先の ID（§5.3「元 ID と操作履歴を残す」） */
  mergedIntoId?: string | null;
  /**
   * （crm-leads-server の追加）名刺から登録した見込み客の名刺（crm の名刺 = 未確認の連絡先の行・§15.4）。
   * 名刺の確認（CARD-03）とは独立で、確認・統合が済むとその連絡先が contactId に入る
   */
  cardId?: string | null;
}

// ---- 入力 ----

const zLeadFields = {
  displayName: zNonEmpty.max(100),
  companyName: zOptionalText(200),
  email: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
  phone: zOptionalText(50),
  title: zOptionalText(100),
  departmentName: zOptionalText(100),
  address: zOptionalText(500),
  website: zOptionalText(500),
  desiredTiming: zOptionalText(100),
  budgetRange: zOptionalText(100),
  inquiryType: zOptionalText(50),
  notes: zOptionalText(5000),
  consentMarketing: z.boolean().nullable().optional(),
};

/** 手入力・電話受付・名刺からの登録（§1 の図の「手入力」）。body を渡すと問い合わせも 1 件作る */
export const zCreateLead = z
  .object({
    ...zLeadFields,
    sourceChannel: z.enum(['phone', 'card', 'manual', 'api']).default('manual'),
    sourceLabel: zOptionalText(200),
    /** 問い合わせ内容。渡すと crm_inquiries に 1 件残す */
    body: zOptionalText(10000),
    ownerId: zUuid.nullable().optional(),
    teamDepartmentId: zUuid.nullable().optional(),
    customerId: zUuid.nullable().optional(),
    contactId: zUuid.nullable().optional(),
    /** 名刺からの登録（§15.4）。名刺の ID を残す */
    cardId: zUuid.nullable().optional(),
  })
  .refine((v) => Boolean(v.email || v.phone), { message: 'メールアドレスか電話番号のどちらかを入れてください', path: ['email'] });
export type CreateLeadInput = z.infer<typeof zCreateLead>;

/**
 * 編集。version は楽観ロック（食い違えば 409 `lead_version_conflict`）。
 * （crm-leads-server の追加）customerId / contactId: 既存の取引先・連絡先への結び付け（null で外す。読める相手だけ）
 */
export const zUpdateLead = z
  .object({ ...zLeadFields, customerId: zUuid.nullable().optional(), contactId: zUuid.nullable().optional(), version: z.number().int().min(1) })
  .partial({ displayName: true });
export type UpdateLeadInput = z.infer<typeof zUpdateLead>;

/** SFA §15.5 優先度（規則で決める。zLeadListQuery.priority / LeadSummary.priority / zLeadPrioritySettings） */
export const LEAD_PRIORITIES = ['high', 'normal', 'low'] as const;
export type LeadPriority = (typeof LEAD_PRIORITIES)[number];
export const LEAD_SORTS = ['received_desc', 'due_asc', 'updated_desc', 'company_asc', 'priority_desc'] as const;
export const zLeadListQuery = z.object({
  /** 氏名・会社名・メール・電話の部分一致 */
  q: z.string().trim().max(200).optional(),
  status: z
    .preprocess((v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v), z.array(z.enum(LEAD_STATUSES)))
    .optional(),
  ownerId: zUuid.optional(),
  /** 未割当だけ（責任者の一覧・§5.2） */
  unassigned: zQueryBool,
  /** 初回対応の期限超過だけ */
  overdue: zQueryBool,
  /** （crm-leads-server の追加）未対応だけ（new / assigned で初回対応が無い。営業ホームの「未対応」= LeadHomeSummary.untouched と同じ条件） */
  untouched: zQueryBool,
  /**
   * （crm-dormant の追加・§15.3.3）休眠だけ: 進行中・匿名化 / 統合されていない見込み客のうち、最終接点（無ければ登録日時）と
   * 問い合わせの受付日時の新しい方が会社設定 `dormantDays` 日より前のもの（公開 I/F の `dormantLeads` と同じ条件。担当の絞り込みは ownerId / 権限スコープのまま）
   */
  dormant: zQueryBool,
  /** （SFA §15.3.2）担当者が利用停止中の見込み客だけ（未割当と同じ人に見せる） */
  ownerSuspended: zQueryBool,
  /** （SFA §15.5）優先度で絞る */
  priority: z.enum(LEAD_PRIORITIES).optional(),
  inquiryType: z.string().trim().max(50).optional(),
  sourceChannel: z.enum(INQUIRY_CHANNELS).optional(),
  customerId: zUuid.optional(),
  formId: zUuid.optional(),
  /** 対応終了・迷惑送信・商談化済みも含める（既定は進行中だけ） */
  includeClosed: zQueryBool,
  sort: z.enum(LEAD_SORTS).default('received_desc'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type LeadListQuery = z.infer<typeof zLeadListQuery>;

/** 担当割当（§5.2）。ownerId = null で未割当に戻す */
export const zAssignLead = z.object({
  ownerId: zUuid.nullable(),
  reason: zOptionalText(500),
  version: z.number().int().min(1).optional(),
});
export type AssignLeadInput = z.infer<typeof zAssignLead>;

/** 状態遷移（§5.3）。start = 対応開始（working）/ qualify = 商談化候補 / close = 対応終了 / spam / reopen = 再開（理由必須） */
export const zTransitionLead = z
  .object({
    action: z.enum(['start', 'qualify', 'close', 'spam', 'reopen']),
    closeReason: z.enum(LEAD_CLOSE_REASONS).optional(),
    reason: zOptionalText(500),
    version: z.number().int().min(1).optional(),
  })
  .refine((v) => v.action !== 'close' || Boolean(v.closeReason), { message: '対応終了の理由を選んでください', path: ['closeReason'] })
  .refine((v) => v.action !== 'reopen' || Boolean(v.reason), { message: '再開の理由を入れてください', path: ['reason'] });
export type TransitionLeadInput = z.infer<typeof zTransitionLead>;

/** 統合（§5.3）。fromLeadId の問い合わせ・履歴を intoLeadId に移し、from は closed(duplicate) にする。元 ID と操作履歴を残す */
export const zMergeLeads = z.object({
  fromLeadId: zUuid,
  /** 残す値。省略した項目は into の値を残す */
  keep: z
    .object({
      displayName: z.enum(['into', 'from']).optional(),
      companyName: z.enum(['into', 'from']).optional(),
      email: z.enum(['into', 'from']).optional(),
      phone: z.enum(['into', 'from']).optional(),
    })
    .optional(),
});
export type MergeLeadsInput = z.infer<typeof zMergeLeads>;

/**
 * 対応の記録（§5.2・§15.2.3）。見込み客のタイムライン（activities）に方向・結果付きで残し、
 * inquiryId を渡すとその問い合わせの「初回対応」に数える（counts = 発信で相手に到達 / 担当者の送信 / 訪問 / 会議）
 */
export const zLeadActivity = z.object({
  type: z.enum(ACTIVITY_TYPES),
  direction: z.enum(ACTIVITY_DIRECTIONS).default('outbound'),
  outcome: z.enum(ACTIVITY_OUTCOMES).default('none'),
  body: zOptionalText(5000),
  occurredAt: z.string().datetime({ offset: true }).optional(),
  /** どの問い合わせへの対応か。未対応の問い合わせが複数あるときは必須（サーバが 400 で聞き返す） */
  inquiryId: zUuid.nullable().optional(),
  dealId: zUuid.nullable().optional(),
});
export type LeadActivityInput = z.infer<typeof zLeadActivity>;

/** 一括操作（§5.6）。1 件ずつ履歴を残す。skipped には理由を付けて返す */
export const zLeadBulkAction = z
  .object({
    ids: z.array(zUuid).min(1).max(500),
    action: z.enum(['assign', 'close', 'spam', 'set_source']),
    ownerId: zUuid.nullable().optional(),
    closeReason: z.enum(LEAD_CLOSE_REASONS).optional(),
    reason: zOptionalText(500),
    sourceLabel: zOptionalText(200),
  })
  .refine((v) => v.action !== 'assign' || v.ownerId !== undefined, { message: '担当者を指定してください', path: ['ownerId'] })
  .refine((v) => v.action !== 'close' || Boolean(v.closeReason), { message: '対応終了の理由を選んでください', path: ['closeReason'] })
  .refine((v) => v.action !== 'set_source' || Boolean(v.sourceLabel), { message: '流入元を入れてください', path: ['sourceLabel'] });
export type LeadBulkActionInput = z.infer<typeof zLeadBulkAction>;
export interface LeadBulkResult {
  updated: number;
  skipped: Array<{ id: string; reason: string }>;
}

// ---- 名簿の CSV 取込（§5.6）。既存の取引先 CSV 取込と同じ 2 段（プレビュー → 確定）----

export const zLeadImportPreview = z.object({
  /** UTF-8（BOM 可）または Shift_JIS を実装側で判定。1 行目は見出し */
  csv: z.string().min(1).max(5_000_000),
  /** 展示会名・名簿名（必須・§5.6） */
  sourceLabel: zNonEmpty.max(200),
});
export type LeadImportPreviewInput = z.infer<typeof zLeadImportPreview>;

export interface LeadImportRow {
  rowNo: number;
  values: Partial<Record<(typeof LEAD_FORM_STANDARD_FIELDS)[number] | 'notes', string>>;
  /** create = 新規 / match_contact = 既存の連絡先に一致（メール完全一致）/ duplicate = 進行中の見込み客と重複 / error = 取り込めない */
  action: 'create' | 'match_contact' | 'duplicate' | 'error';
  matchedId: string | null;
  matchedLabel: string | null;
  errors: string[];
}
export interface LeadImportPreview {
  id: string;
  sourceLabel: string;
  total: number;
  counts: Record<LeadImportRow['action'], number>;
  /** 先頭 200 行までを返す。全行はサーバに保持し、確定時に使う */
  rows: LeadImportRow[];
  expiresAt: Date;
}

export const zLeadImportCommit = z.object({
  ownerId: zUuid.nullable().optional(),
  teamDepartmentId: zUuid.nullable().optional(),
  /** duplicate の行を飛ばす（既定 true）。false なら別の見込み客として作る */
  skipDuplicates: z.boolean().default(true),
  /** match_contact の行は既存の連絡先・取引先に結び付けて作る（既定 true） */
  linkMatched: z.boolean().default(true),
});
export type LeadImportCommitInput = z.infer<typeof zLeadImportCommit>;
export interface LeadImportResult {
  created: number;
  linked: number;
  skipped: number;
  errors: number;
}

// ---- 公開フォーム（§5.1・§5.11）----

export const zLeadFormField = z.object({
  key: z.string().trim().min(1).max(50).regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
  label: zNonEmpty.max(100),
  type: z.enum(LEAD_FORM_FIELD_TYPES),
  required: z.boolean().default(false),
  /** select の選択肢 */
  options: z.array(zNonEmpty.max(100)).max(50).optional(),
  placeholder: zOptionalText(200),
  /** 標準項目（LEAD_FORM_STANDARD_FIELDS）は見込み客の列に写る。それ以外は inquiry.fields に残る */
  standard: z.boolean().default(false),
});
export type LeadFormField = z.infer<typeof zLeadFormField>;

export const zLeadFormAssignment = z.object({
  mode: z.enum(LEAD_ASSIGNMENT_MODES).default('none'),
  /** fixed = 先頭の人 / round_robin = この並びで順番（在籍中・利用可能・営業権限のある人だけに絞る） */
  assigneeIds: z.array(zUuid).max(50).default([]),
  /** 受付先チーム（未割当をこの部署の責任者に見せる） */
  teamDepartmentId: zUuid.nullable().optional(),
});

export const zLeadFormInput = z.object({
  name: zNonEmpty.max(100),
  /** 見出しと案内文（公開ページの上部） */
  title: zOptionalText(200),
  description: zOptionalText(2000),
  fields: z.array(zLeadFormField).min(1).max(40),
  /** 問い合わせ種別の選択肢（会社ごとに変更可・§5.1） */
  inquiryTypes: z.array(zNonEmpty.max(50)).max(20).default([...DEFAULT_INQUIRY_TYPES]),
  /** 個人情報の案内（会社が用意した文面）と版。公開版は不変 */
  privacyNotice: zOptionalText(10000),
  privacyNoticeVersion: zOptionalText(50),
  /** メール配信への同意欄を出す（受付と独立した任意項目・§5.1） */
  consentField: z.boolean().default(false),
  completionMessage: zOptionalText(1000),
  assignment: zLeadFormAssignment.default({ mode: 'none', assigneeIds: [] }),
  /** 自動受付メール（§5.7）。テンプレートは core の通知テンプレート */
  /**
   * 自動受付メール（§5.7）。subject / body はフォームごとの文面（差し込み: {{displayName}} {{companyName}} {{inquiryType}} {{receiptNo}} {{ownerName}}）。
   * core の通知テンプレートは社外宛てに描画する口が無いので、フォームの定義に文面を持つ。templateCode は将来のために残す（1A では使わない）
   */
  autoReply: z
    .object({
      enabled: z.boolean().default(false),
      templateCode: zOptionalText(100),
      subject: zOptionalText(200),
      body: zOptionalText(5000),
      /**
       * （gap-crm-b の追加・§5.7）差出人の表示名（アドレスは会社の MAIL_FROM のまま）。差し込み {{tenantName}}（会社名）{{ownerName}}（担当者名）。
       * 空なら「会社名 担当者名」（担当者が決まっていなければ会社名だけ）。担当者個人のアドレスからは送らない
       */
      fromName: zOptionalText(100),
      /** （gap-crm-b の追加・§5.7）返信先（Reply-To。info@ など）。空なら差出人と同じ（送信専用のアドレス） */
      replyTo: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
    })
    .default({ enabled: false }),
  /** 社内チャットへの通知（§5.8）。チャンネル ID は chat の公開 API で存在確認する */
  chatChannelId: zUuid.nullable().optional(),
  /** 埋め込み元ドメインの許可リスト（§5.11）。空なら本サービスの公開ページだけ */
  allowedOrigins: z.array(z.string().trim().max(200)).max(20).default([]),
  botCheck: z.enum(LEAD_BOT_CHECKS).default('none'),
});
export type LeadFormInput = z.infer<typeof zLeadFormInput>;

export interface LeadFormSummary {
  id: string;
  name: string;
  status: LeadFormStatus;
  /** 公開識別子（32 文字以上・推測不能・§5.11）。公開 URL は /f/<publicId> */
  publicId: string;
  /** 公開中の版番号（下書きだけなら null） */
  publishedVersion: number | null;
  /** 下書きの版番号（公開後の変更は新しい版・§5.1） */
  draftVersion: number;
  publishedAt: Date | null;
  stoppedAt: Date | null;
  submissionCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface LeadFormDetail extends LeadFormSummary {
  /** 下書き（編集中）の内容 */
  draft: LeadFormInput;
  /** 公開中の内容（無ければ null） */
  published: LeadFormInput | null;
}

/** 公開ページ・埋め込みが読む定義（認証なし・§5.1）。内部 ID は返さない */
export interface PublicLeadForm {
  publicId: string;
  version: number;
  companyName: string;
  title: string | null;
  description: string | null;
  fields: LeadFormField[];
  inquiryTypes: string[];
  privacyNotice: string | null;
  privacyNoticeVersion: string | null;
  consentField: boolean;
  botCheck: LeadBotCheck;
  /** Turnstile / reCAPTCHA のサイトキー（公開値） */
  botSiteKey: string | null;
}

/** 公開の受付（§5.2）。submissionId はブラウザが作る UUID（再送は 1 件にまとめる） */
export const zPublicLeadSubmission = z.object({
  submissionId: zUuid,
  /** 項目キー → 値。標準項目のキーは LEAD_FORM_STANDARD_FIELDS */
  fields: z.record(z.string().max(50), z.union([z.string().max(10000), z.boolean()])),
  consentMarketing: z.boolean().optional(),
  /** ハニーポット（表示しない欄）。値があれば受け付けたふりをして捨てる */
  website_url: z.string().max(500).optional(),
  botToken: z.string().max(5000).optional(),
  referrer: z.string().max(1000).optional(),
  utm: z.record(z.string().max(50), z.string().max(200)).optional(),
  /**
   * （gap-crm-b の追加）iframe で埋め込んだときの親ページ（埋め込み用のコードが iframe の URL の parent に付ける）。
   * サーバは http(s) だけを受け、クエリと # を落として問い合わせの pageUrl に残す。referrer / utm は親ページの値が入ってくる
   */
  pageUrl: z.string().max(2000).optional(),
});
export type PublicLeadSubmissionInput = z.infer<typeof zPublicLeadSubmission>;

/** 受付完了。内部の顧客・商談 ID は返さない（§5.1） */
export interface PublicLeadSubmissionResult {
  ok: true;
  receiptNo: string;
  message: string;
}

export const zLeadFormListQuery = z.object({
  status: z.enum(LEAD_FORM_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
});

/** 送信履歴（フォーム管理の「送信履歴」・§4） */
export interface LeadFormSubmissionEntry {
  inquiryId: string;
  leadId: string;
  receivedAt: Date;
  companyName: string | null;
  displayName: string;
  inquiryType: string | null;
  ownerId: string | null;
  ownerName: string | null;
  status: LeadStatus;
}

// ---- 割当規則（§5.2）。フォームの割当設定より先に評価する。上から順に最初に当たった規則で決める ----

export const zLeadAssignmentRuleInput = z.object({
  name: zNonEmpty.max(100),
  /** 小さいほど先に評価 */
  order: z.number().int().min(0).max(1000).default(100),
  active: z.boolean().default(true),
  conditions: z
    .object({
      inquiryTypes: z.array(z.string().trim().max(50)).max(20).optional(),
      formIds: z.array(zUuid).max(20).optional(),
      channels: z.array(z.enum(INQUIRY_CHANNELS)).max(10).optional(),
      /** 本文・会社名に含まれる語（いずれか） */
      keywords: z.array(zNonEmpty.max(50)).max(20).optional(),
      // ---- SFA §15.5 テリトリー（追加）: 地域・商品別の担当範囲 ----
      /** 地域（都道府県名など）。見込み客の住所（無ければ問い合わせの項目 address）の先頭一致（いずれか） */
      regions: z.array(zNonEmpty.max(30)).max(50).optional(),
      /** 商品。問い合わせの項目（会社設定 routing.productFieldKey）の値の一致か、本文・件名に含まれる語（いずれか） */
      products: z.array(zNonEmpty.max(50)).max(50).optional(),
    })
    .default({}),
  assignment: zLeadFormAssignment,
});
export type LeadAssignmentRuleInput = z.infer<typeof zLeadAssignmentRuleInput>;

export interface LeadAssignmentRule extends LeadAssignmentRuleInput {
  id: string;
  version: number;
  /** round_robin の現在位置（次に割り当てる assigneeIds の添字） */
  cursor: number;
  createdAt: Date;
  updatedAt: Date;
}


// ---- SFA §15.5 優先度付け（規則）。crm-lead.dto.ts の zLeadSettings.priority ----


/** 優先度の規則 1 つ。when の条件（配列は「いずれか」・キー同士は AND）に当たれば points を加える */
export const zLeadPriorityRule = z.object({
  name: zNonEmpty.max(100),
  active: z.boolean().default(true),
  when: z
    .object({
      inquiryTypes: z.array(z.string().trim().max(50)).max(20).optional(),
      channels: z.array(z.enum(INQUIRY_CHANNELS)).max(10).optional(),
      formIds: z.array(zUuid).max(20).optional(),
      /** 予算帯（見込み客の budgetRange の文字列に一致） */
      budgetRanges: z.array(z.string().trim().max(50)).max(20).optional(),
      /** 希望時期（desiredTiming の文字列に一致） */
      desiredTimings: z.array(z.string().trim().max(50)).max(20).optional(),
      /** 企業規模（問い合わせの項目 companySizeFieldKey の数値がこの範囲） */
      companySizeMin: z.number().int().min(0).optional(),
      companySizeMax: z.number().int().min(0).optional(),
      /** 本文・件名・会社名に含まれる語（いずれか） */
      keywords: z.array(zNonEmpty.max(50)).max(20).optional(),
    })
    .default({}),
  /** 加点（負も可） */
  points: z.number().int().min(-100).max(100),
});
export type LeadPriorityRule = z.infer<typeof zLeadPriorityRule>;

export const zLeadPrioritySettings = z.object({
  rules: z.array(zLeadPriorityRule).max(50).default([]),
  /** 合計点がこの値以上なら high */
  highAt: z.number().int().min(-1000).max(1000).default(10),
  /** 合計点がこの値以下なら low */
  lowAt: z.number().int().min(-1000).max(1000).default(-10),
  /** 営業ホームの「新しい問い合わせ」を優先度順（同点は新しい順）にする */
  homeSortByPriority: z.boolean().default(true),
  /** 企業規模の問い合わせ項目のキー（フォームのカスタム項目。例 employees。null = 見ない） */
  companySizeFieldKey: zOptionalText(50),
});
export type LeadPrioritySettings = z.infer<typeof zLeadPrioritySettings>;
// ---- 見込み客の設定（会社ごと・§5.2・§15.2・§15.8）----

export const zLeadSettings = z.object({
  /** 初回対応の期限（営業日）。§15.2.1 の営業日カレンダーで計算。初期値 1 */
  firstResponseBusinessDays: z.number().int().min(0).max(30).default(1),
  /** 期限超過の通知の段階（営業時間）。担当者 → 責任者 → 全体管理者（§15.2.2） */
  escalation: z
    .object({
      managerAfterHours: z.number().int().min(0).max(240).default(8),
      adminAfterHours: z.number().int().min(0).max(720).default(24),
    })
    .default({ managerAfterHours: 8, adminAfterHours: 24 }),
  /** 休眠のしきい値（日・§15.3.3）。初期値 90 */
  dormantDays: z.number().int().min(7).max(730).default(90),
  /** 商談化しなかった見込み客を匿名化するまでの月数（§15.2.4）。初期値 24 */
  retentionMonths: z.number().int().min(1).max(120).default(24),
  /** 受付先チームの既定（フォーム・規則で決まらないとき） */
  defaultTeamDepartmentId: zUuid.nullable().optional(),
  /** 新着・未割当・期限超過を投稿する社内チャンネル（§5.8）。null = 投稿しない */
  chatChannelId: zUuid.nullable().optional(),
  /** 転送受付（§5.5）。有効化すると受付用アドレスを発行する */
  inboundEmail: z.object({ enabled: z.boolean().default(false) }).default({ enabled: false }),
  /**
   * SFA §15.5 優先度付け（規則で決める。AI ではない）: 規則の加点の合計 → high / normal / low。営業ホームの並び順に使う。
   * rules が空なら全員 normal（点数 0）
   */
  priority: zLeadPrioritySettings.default({ rules: [], highAt: 10, lowAt: -10, homeSortByPriority: true, companySizeFieldKey: null }),
  /** SFA §15.5 テリトリー: 商品別の条件に使う問い合わせの項目のキー（フォームのカスタム項目。null = 使わない） */
  routing: z.object({ productFieldKey: zOptionalText(50) }).default({ productFieldKey: 'product' }),
});
export type LeadSettingsInput = z.infer<typeof zLeadSettings>;
export interface LeadSettings extends LeadSettingsInput {
  /** 受付用アドレス（inboundEmail.enabled のとき）。ローカル部は公開識別子 */
  inboundAddress: string | null;
  /** 受信 Webhook の URL（管理画面に表示。秘密は含めない） */
  inboundWebhookPath: string | null;
  /**
   * （crm-leads-server の追加）受信 Webhook の秘密。**発行した直後の応答（有効化した PUT / rotate）にだけ付く**（1 回だけ表示）。
   * Webhook の URL に `?token=<秘密>` を付けるか、ヘッダ `X-Inbound-Token` で送る。保存はハッシュだけ
   */
  inboundSecret?: string;
  /**
   * （gap-crm-b の追加・任意）転送受付の受け口。shared = 単一の受信ドメイン（Mailjet の parse route はサービスで 1 つ・会社は受付用アドレスだけを使う）/
   * per_company = 会社ごとの Webhook（inboundWebhookPath と inboundSecret を事業者に登録する従来の形）。型の別名は crm-lead-assist.dto.ts の LeadInboundRouting
   */
  inboundRouting?: 'shared' | 'per_company';
}

// ---- 営業ホーム向けの集計（§4）。sales が CrmPublicApi.leadHomeSummary で受け取り、/sales/home に合成する ----

export interface LeadHomeInquiryRow {
  leadId: string;
  inquiryId: string | null;
  receivedAt: Date;
  companyName: string | null;
  displayName: string;
  inquiryType: string | null;
  sourceChannel: InquiryChannel;
  ownerId: string | null;
  ownerName: string | null;
  firstResponseDueAt: Date | null;
  overdue: boolean;
  // ---- SFA 段階 3（追加・任意）----
  priority?: LeadPriority;
  ownerStatus?: LeadOwnerStatus | null;
}

export interface LeadHomeSummary {
  /** 未対応（new / assigned で初回対応が無い） */
  untouched: number;
  unassigned: number;
  overdue: number;
  /** 期間内に作成された見込み客 */
  newInPeriod: number;
  /** 未対応の新しい順（最大 limit 件） */
  newInquiries: LeadHomeInquiryRow[];
  // ---- SFA 段階 3（追加・任意）----
  /** 担当者が利用停止中の進行中の見込み客（未割当と同じ人に見せる・§15.3.2） */
  ownerSuspended?: number;
  /**
   * （gap-sales の追加・任意）newInquiries の並び（decisions §AC の残課題「SalesHome に並び順を返す」）。
   * priority_desc = 会社設定 priority.homeSortByPriority が true（優先度の高い順・同点は新しい順）/ received_desc = 受付の新しい順
   */
  order?: 'priority_desc' | 'received_desc';
}

// ---- 取引先・連絡先からの逆引き（§5.9）----

export const zLeadsForCustomerQuery = z.object({
  includeClosed: zQueryBool,
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

// ---- crm-leads-server の追加（契約への追加のみ。既存の名前・意味は変えていない）----

/** 進行中（一覧の既定・「未対応」「期限超過」の母数）。converted / closed / spam は終わった見込み客 */
export const LEAD_OPEN_STATUSES = ['new', 'assigned', 'working', 'qualified'] as const satisfies readonly LeadStatus[];

/** 既定の問い合わせ種別の表示名（DEFAULT_INQUIRY_TYPES のキー → 画面の文言）。会社が足した種別は値をそのまま出す */
export const LEAD_INQUIRY_TYPE_LABELS: Record<(typeof DEFAULT_INQUIRY_TYPES)[number], string> = {
  document_request: '資料請求',
  quote: '見積相談',
  consultation: '導入相談',
  other: 'その他',
};

/** 対応終了の理由の表示名 */
export const LEAD_CLOSE_REASON_LABELS: Record<LeadCloseReason, string> = {
  no_need: '必要なし',
  no_response: '連絡がつかない',
  budget: '予算が合わない',
  timing: '時期が合わない',
  competitor: '他社に決定',
  duplicate: '重複（統合済み）',
  other: 'その他',
};

/** 409 / 400 の details.reason（画面・アプリが出し分けに使う） */
export const LEAD_ERROR_REASONS = {
  /** 状態遷移の表（§5.3）に無い操作。details: { from, action } */
  invalidTransition: 'lead_invalid_transition',
  /** 版番号の食い違い（楽観ロック）。details: { currentVersion } */
  versionConflict: 'lead_version_conflict',
  /** 別の操作 ID で商談化済み。details: { dealId } */
  alreadyConverted: 'lead_already_converted',
  /** 未対応の問い合わせが複数ある対応の記録で、どの問い合わせか未指定（400）。details: { candidates: InquirySummary[] } */
  inquiryRequired: 'inquiry_required',
  /** 公開フォームの停止中（410） */
  formStopped: 'lead_form_stopped',
} as const;

/** 見込み客のタイムライン（activities）の 1 行。§15.2.3 の方向・結果・対象の問い合わせ付き */
export interface LeadActivitySummary {
  id: string;
  leadId: string | null;
  customerId: string | null;
  contactId: string | null;
  dealId: string | null;
  inquiryId: string | null;
  userId: string | null;
  userName: string | null;
  type: (typeof ACTIVITY_TYPES)[number];
  direction: ActivityDirection | null;
  outcome: ActivityOutcome | null;
  body: string | null;
  /** §15.2.3 の「初回対応に数える」種類か（発信で到達した電話 / 担当者の送信メール / 訪問 / 会議） */
  countsAsResponse: boolean;
  occurredAt: Date;
  createdAt: Date;
  /** SFA 1B の追加（任意）: 元レコード。共有メール（refType = crm_email_record）の行から本文のドロワーを開く */
  refType?: string | null;
  refId?: string | null;
  /** SFA 段階 5（§9.4・任意）: 通話の長さ（秒）。telephony が記録した電話に付く。画面は「N 分」と出す */
  durationSec?: number | null;
}

/** ページング付きの一覧（見込み客のタイムライン・フォームの送信履歴） */
export const zLeadPageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type LeadPageQuery = z.infer<typeof zLeadPageQuery>;

/** 削除請求による匿名化（§15.2.4・admin）。理由は監査ログに残す */
export const zAnonymizeLead = z.object({ reason: zOptionalText(500) });
export type AnonymizeLeadInput = z.infer<typeof zAnonymizeLead>;

/** 見込み客の作成に使う既存 DTO の再エクスポート（sales の商談化が「新規作成」で使う） */
export { zCreateContact, zCreateCustomer };
